import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import Toast from '@/components/base/Toast';
import { systemSettings } from '@/mocks/settings';
import {
  createItem,
  deleteItem,
  fetchDurationSettings,
  fetchItems,
  setItemActive,
  updateItem,
} from '@/services/itemsService';
import type { ItemDurationSettings, ItemPayload, LaundryItem } from '@/types';
import ConfirmDialog from './components/ConfirmDialog';
import DurationSummary from './components/DurationSummary';
import ItemFormModal from './components/ItemFormModal';
import ItemsTable from './components/ItemsTable';

type ConfirmState = { type: 'toggle' | 'delete'; item: LaundryItem };
type ToastState = { message: string; tone: 'success' | 'error' };

export default function ItemsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState<LaundryItem[] | null>(null);
  const [durations, setDurations] = useState<ItemDurationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<LaundryItem | null>(null);

  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [itemsData, durationsData] = await Promise.all([
        fetchItems(),
        fetchDurationSettings().catch(() => null),
      ]);
      setItems(itemsData);
      setDurations(durationsData);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (message: string, tone: ToastState['tone'] = 'success') => setToast({ message, tone });

  const openAdd = () => {
    setEditingItem(null);
    setFormOpen(true);
  };

  const openEdit = (item: LaundryItem) => {
    setEditingItem(item);
    setFormOpen(true);
  };

  const handleSubmit = async (payload: ItemPayload, id: string | null) => {
    if (id) {
      const updated = await updateItem(id, payload);
      setItems((prev) => prev?.map((item) => (item.id === id ? updated : item)) ?? prev);
      notify(t('items.toast.updated'));
    } else {
      const created = await createItem(payload);
      setItems((prev) => (prev ? [created, ...prev] : [created]));
      notify(t('items.toast.added'));
    }
  };

  const activateItem = async (item: LaundryItem) => {
    setBusyId(item.id);
    try {
      await setItemActive(item.id, true);
      setItems((prev) => prev?.map((row) => (row.id === item.id ? { ...row, active: true } : row)) ?? prev);
      notify(t('items.toast.activated'));
    } catch {
      notify(t('common.errorText'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleClick = (item: LaundryItem) => {
    if (item.active) {
      setConfirm({ type: 'toggle', item });
    } else {
      void activateItem(item);
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      if (confirm.type === 'delete') {
        await deleteItem(confirm.item.id);
        setItems((prev) => prev?.filter((row) => row.id !== confirm.item.id) ?? prev);
        notify(t('items.toast.deleted'));
      } else {
        await setItemActive(confirm.item.id, false);
        setItems((prev) => prev?.map((row) => (row.id === confirm.item.id ? { ...row, active: false } : row)) ?? prev);
        notify(t('items.toast.deactivated'));
      }
      setConfirm(null);
    } catch {
      notify(t('common.errorText'), 'error');
    } finally {
      setConfirmLoading(false);
    }
  };

  const isEmpty = !loading && !error && items !== null && items.length === 0;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground-950 md:text-xl">{t('items.title')}</h1>
          <p className="mt-1 text-sm text-foreground-500">{t('items.subtitle')}</p>
        </div>
        <Button variant="primary" size="md" icon="ri-add-line" onClick={openAdd}>
          {t('items.add')}
        </Button>
      </header>

      <DurationSummary
        durations={durations}
        onOpenSettings={() => navigate('/dashboard/settings?edit=1&highlight=durations')}
      />

      <Card padded={false} className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-4 py-4 md:px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-shirt-line text-xl leading-none" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-base font-semibold text-foreground-950">{t('items.table.item')}</h2>
              <p className="mt-0.5 text-xs text-foreground-500">{t('items.subtitle')}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3 p-4 md:p-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-11 w-11" />
                <Skeleton className="h-5 w-28" />
                <Skeleton className="h-5 flex-1" />
                <Skeleton className="h-5 w-24" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-4 md:p-5">
            <ErrorState
              title={t('items.error')}
              description={t('common.errorText')}
              retryLabel={t('common.retry')}
              onRetry={load}
            />
          </div>
        ) : isEmpty ? (
          <div className="p-4 md:p-5">
            <EmptyState
              icon="ri-shirt-line"
              title={t('items.empty.title')}
              description={t('items.empty.text')}
              action={
                <Button variant="primary" size="md" icon="ri-add-line" onClick={openAdd}>
                  {t('items.empty.action')}
                </Button>
              }
            />
          </div>
        ) : (
          <ItemsTable
            items={items ?? []}
            currency={systemSettings.currency}
            busyId={busyId}
            onEdit={openEdit}
            onToggle={handleToggleClick}
            onDelete={(item) => setConfirm({ type: 'delete', item })}
          />
        )}

        {!loading && !error && items && items.length > 0 ? (
          <div className="border-t border-background-100 bg-background-100/60 px-4 py-3 md:px-5">
            <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
              <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
              {t('items.demoNote')}
            </p>
          </div>
        ) : null}
      </Card>

      <ItemFormModal
        open={formOpen}
        item={editingItem}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={confirm?.type === 'toggle'}
        title={t('items.confirm.deactivate.title')}
        text={t('items.confirm.deactivate.text')}
        confirmLabel={t('items.confirm.deactivate.confirm')}
        cancelLabel={t('items.form.cancel')}
        tone="primary"
        loading={confirmLoading}
        onConfirm={handleConfirm}
        onClose={() => (confirmLoading ? undefined : setConfirm(null))}
      />

      <ConfirmDialog
        open={confirm?.type === 'delete'}
        title={t('items.confirm.delete.title')}
        text={t('items.confirm.delete.text')}
        confirmLabel={t('items.confirm.delete.confirm')}
        cancelLabel={t('items.form.cancel')}
        tone="danger"
        loading={confirmLoading}
        onConfirm={handleConfirm}
        onClose={() => (confirmLoading ? undefined : setConfirm(null))}
      />

      {toast ? <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} /> : null}
    </div>
  );
}